import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormRendererContext } from '../FormRendererContext';
import { FieldDescription, FieldError, FieldLabel, fieldIds } from './shared';
import type { FieldProps } from './FieldProps';

/**
 * The field's value is a blob reference (URL or blob name), not the image
 * itself — same pattern `JobPhoto` already uses (docs/forms-schema.md, API
 * repo). `uploadPhoto` (`FormRendererContext`) does the actual upload; this
 * component only owns picking a file and showing upload/failure state.
 */
export function PhotoField({ field, value, error, onChange }: FieldProps) {
  const { t } = useTranslation();
  const { uploadPhoto } = useFormRendererContext();
  const { inputId, descriptionId, errorId } = fieldIds(field.path);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'failed'>('idle');

  async function handleFile(file: File | undefined) {
    if (!file) {
      return;
    }

    if (!uploadPhoto) {
      // No upload mechanic wired up — this renderer has no caller yet
      // (M1-19/M1-20 build real submission storage). A local-only preview
      // keeps the field genuinely functional in the meantime rather than
      // silently doing nothing when someone picks a file.
      const reader = new FileReader();
      reader.onload = () => onChange(typeof reader.result === 'string' ? reader.result : undefined);
      reader.readAsDataURL(file);
      return;
    }

    setStatus('uploading');
    try {
      const reference = await uploadPhoto(file);
      onChange(reference);
      setStatus('idle');
    } catch {
      setStatus('failed');
    }
  }

  return (
    <div>
      <FieldLabel htmlFor={inputId} required={field.required}>
        {field.schema.title ?? field.key}
      </FieldLabel>
      <FieldDescription id={descriptionId} text={field.schema.description} />
      <input
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        className="text-sm"
        onChange={(event) => void handleFile(event.target.files?.[0])}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
      />
      {status === 'uploading' ? (
        <p role="status" className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {t('forms.photo.uploading')}
        </p>
      ) : null}
      {status === 'failed' ? (
        <p role="alert" className="mt-1 text-xs text-[var(--color-destructive)]">
          {t('forms.photo.failed')}
        </p>
      ) : null}
      {typeof value === 'string' && value ? (
        <img src={value} alt="" className="mt-2 h-24 w-24 rounded-md border object-cover" />
      ) : null}
      <FieldError id={errorId} message={error} />
    </div>
  );
}
